import { useCallback } from 'react'
import { EditRowActionOptions } from 'components/CIMSTable/CIMSTableContext/types/actions/row'
import { LabeledValueType } from 'components/CIMSSelect/types'
import { simpleEditNotification } from 'hooks/notificationHandler'
import { useEditWorkflowPermissionMutation } from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { usePermissionsContext } from '../../../PermissionsContext'
import { buildEditPermissionInput } from '../../../utils'
import { getPermissionActionMetadata } from '../../../metadata'
import { PermissionFormValues, WorkflowPermissionRecord } from '../../../types'
import PermissionFormItems from '../components/PermissionFormItems'
import { usePermissionFormValues } from './usePermissionFormValues'

export function useEdit(): EditRowActionOptions<
  WorkflowPermissionRecord,
  PermissionFormValues
> {
  const {
    metadata: { fieldMap, statusMap },
    refetch,
    users: { byId: usersById, label: userLabel, tooltip: userTooltip },
  } = usePermissionsContext()
  const [editPermission] = useEditWorkflowPermissionMutation(
    DefaultMutationHookOptions
  )
  const toUserLabeledValues = useCallback(
    (ids: string[]): LabeledValueType[] =>
      ids.map((id) => {
        if (!usersById.has(id)) {
          return {
            value: id,
            label: `Unknown user (${id})`,
            tooltip: 'This user could not be found.',
            deleted: true,
          }
        }
        return {
          value: id,
          label: userLabel(id),
          tooltip: userTooltip(id),
        }
      }),
    [userLabel, userTooltip, usersById]
  )
  const toFormValues = usePermissionFormValues({
    fieldMap,
    statusMap,
    toUserLabeledValues,
  })

  return {
    permission: 'admin',
    modal: {
      type: 'simple',
      width: 'wide',
      recordName: ({ action }) => getPermissionActionMetadata(action).label,
      initialValues: toFormValues,
      body: ({ action }) => <PermissionFormItems readOnlyAction={action} />,
      onOk: async (record, values: PermissionFormValues) => {
        await editPermission({
          variables: {
            input: buildEditPermissionInput(record, values),
          },
        })
        await refetch()
        simpleEditNotification('Permission')
      },
    },
  }
}
